import { useMutation, useQuery } from '@apollo/client'
import { StringHelpers } from '@dai/common'
import { useTypedFormik } from '@dai/components'
import {
  DefaultStatus,
  TypedFormikReturn,
} from '@dai/components/src/formik/types'
import { useTheme } from '@emotion/react'
import { Add, Close, Search } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { useDebounce } from 'use-debounce'
import * as Yup from 'yup'

import { useSelectedIds } from '@/components/big-filter/useSelectedIds'
import { StoreHeader } from '@/components/stores/StoreHeader'
import { useRefreshContext } from '@/contexts/RefreshContext'
import {
  ActionRepeatSchedule,
  ActionType,
  CREATE_ACTION,
  CreateActionMutation,
  CreateActionMutationVariables,
  GET_MASTER_CATEGORIES,
  GetMasterCategoriesQuery,
  GetMasterCategoriesQueryVariables,
  InputMaybe,
  Scalars,
} from '@/graphql'
import { useGetTenantStores } from '@/hooks/useGetTenantStores'
import useT from '@/i18n/useT'
import { DateHelpers } from '@/utils/date.helpers'
import { TolgeeHelpers } from '@/utils/tolgee.helpers'

import cold_vault_1 from '../../../../public/static/img/displays/cold_vault_1.png'
import cold_vault_2 from '../../../../public/static/img/displays/cold_vault_2.png'
import { DAISelect } from '../components/DAISelect'
import { MUIDatePicker } from '../components/MUIDatePicker'
import { MUITimePicker } from '../components/MUITimePicker'
import { ActionTypeCard } from './ActionTypeCard'

type StepThreeProps = {
  formik: TypedFormikReturn<CreateFormValues, DefaultStatus>
  dueTimes: string[]
  setDueTimes: React.Dispatch<React.SetStateAction<string[]>>
  setTimeAtIndex: (index: number, time: string) => void
}

const StepThree: React.FC<StepThreeProps> = props => {
  const { formik, dueTimes, setDueTimes, setTimeAtIndex } = props
  const { t } = useT()
  return (
    <Stack>
      <Stack direction={'row'} spacing={2} alignItems="center" mb={8}>
        <Typography variant="h6">Start on</Typography>
        <MUIDatePicker
          variant="outlined"
          label={t('createRestockAction.dueDate')}
          date={formik.values.dueDate ? dayjs(formik.values.dueDate) : null}
          onDateChange={date => {
            formik.setValues({
              ...formik.values,
              dueDate: date?.format('YYYY-MM-DD'),
            })
          }}
          error={Boolean(formik.errors.dueDate)}
        />
        <Typography variant="h6">repeat</Typography>
        <DAISelect
          title={t('createRestockAction.repeat')}
          errorMessage={formik.errors.repeatSchedule}
          options={[
            {
              value: ActionRepeatSchedule.DAILY,
              label: t('dateHelper.repeatSchedule.daily'),
            },
            {
              value: ActionRepeatSchedule.WEEKLY,
              label: `${DateHelpers.getONString(t, ActionRepeatSchedule.WEEKLY, formik.values.dueDate)}`,
            },
            {
              value: ActionRepeatSchedule.MONTHLY,
              label: `${DateHelpers.getONString(t, ActionRepeatSchedule.MONTHLY, formik.values.dueDate)}`,
            },
            {
              value: ActionRepeatSchedule.YEARLY,
              label: `${DateHelpers.getONString(t, ActionRepeatSchedule.YEARLY, formik.values.dueDate)}`,
            },
          ]}
          value={formik.values.repeatSchedule}
          handleChange={schedule =>
            formik.setValues({
              ...formik.values,
              repeatSchedule: schedule as ActionRepeatSchedule,
            })
          }
        />
      </Stack>
      <Stack direction={'row'} spacing={2} justifyContent={'center'}>
        {/* TODO: SHOW THE BUSY TIMES GRAPH HERE */}
        <Stack spacing={2}>
          <Stack spacing={3}>
            {dueTimes.map((time, index) => {
              return (
                <Stack
                  key={index}
                  direction={'row'}
                  spacing={2}
                  alignItems={'center'}
                >
                  <MUITimePicker
                    label={t('createRestockAction.dueTime')}
                    dateTime={time ? dayjs(time) : null}
                    onDateTimeChange={newDate => {
                      setTimeAtIndex(index, newDate?.toISOString() || '')
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      setDueTimes(dueTimes.filter((_, i) => i !== index))
                    }}
                  >
                    <Close />
                  </IconButton>
                </Stack>
              )
            })}
          </Stack>
          <Button
            type="button"
            startIcon={<Add />}
            onClick={() => setDueTimes([...dueTimes, ''])}
          >
            {t('common.addMore')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export type CreateFormValues = {
  title: Scalars['String']['input']
  description?: InputMaybe<Scalars['String']['input']>
  dueDate?: InputMaybe<string>
  dueDateTime?: InputMaybe<string>
  endDate?: InputMaybe<Scalars['DateTime']['input']>
  repeatSchedule?: ActionRepeatSchedule | ''
  masterCategoryId: string
  actionType: ActionType | ''
}

export const validationSchema = Yup.object().shape({
  title: Yup.string().required('Required'),
  masterCategoryId: Yup.string().required('Required'),
  actionType: Yup.string().required('Required'),
})

type Props = {
  open: boolean
  onClose: () => void
  onSuccess?: (actionGroup?: CreateActionMutation['createAction']) => void
  storeId?: string
}

export const CreateRestockAction: React.FC<Props> = props => {
  const { open, onClose, onSuccess, storeId } = props
  const { refresh } = useRefreshContext()
  const [activeStep, setActiveStep] = React.useState(0)
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean
  }>({})
  const { t } = useT()

  const getActionTypeLabel = (actionType: ActionType | '') => {
    switch (actionType) {
      case ActionType.PRODUCT_CAPTURE:
        return t(
          'createRestockAction.actionType.planogramComplianceTitle',
          'Planogram Compliance',
        )
      case ActionType.PICTURE:
        return t(
          'createRestockAction.actionType.singlePhotoTitle',
          'Single Photo',
        )
      case ActionType.SIMPLE:
        return t('createRestockAction.actionType.noPhotoTitle', 'No Photo')
      default:
        return ''
    }
  }

  const steps = [
    t('createRestockAction.step_1'),
    t('createRestockAction.selectActionTypeStep', 'Select Type'),
    t('createRestockAction.step_2'),
    t('createRestockAction.step_3'),
    t('createRestockAction.reviewStep', 'Review'),
  ]

  const [createAction] = useMutation<
    CreateActionMutation,
    CreateActionMutationVariables
  >(CREATE_ACTION)

  const { data: categoryData } = useQuery<
    GetMasterCategoriesQuery,
    GetMasterCategoriesQueryVariables
  >(GET_MASTER_CATEGORIES, { fetchPolicy: 'cache-and-network' })
  const categories =
    categoryData?.masterCategories.results.map(c => ({
      label: StringHelpers.titleCase(c.name),
      value: c.id,
    })) || []
  const initialValues: CreateFormValues = {
    title: '',
    description: '',
    repeatSchedule: ActionRepeatSchedule.DAILY,
    dueDate: dayjs().format('YYYY-MM-DD'),
    masterCategoryId: '',
    actionType: '',
  }

  const [selectedStores, storeSelected, setSelectedStores] = useSelectedIds()
  const formik = useTypedFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setStatus }) => {
      try {
        if (!values.title) {
          setStatus({
            success: false,
            message: t('createRestockAction.error.titleRequired'),
          })
          return
        }
        if (!values.masterCategoryId) {
          setStatus({
            success: false,
            message: t('createRestockAction.error.categoryRequired'),
          })
          return
        }
        if (!values.actionType) {
          setStatus({
            success: false,
            message: t('createRestockAction.error.actionTypeRequired'),
          })
          return
        }

        const allPromises: Promise<any>[] = []
        dueTimes
          .filter(t => t !== '')
          .forEach(time => {
            const createActionInput: CreateActionMutationVariables['input'] = {
              title: formik.values.title,
              masterCategoryId: formik.values.masterCategoryId,
              isRestock: true,
              actionType: formik.values.actionType as ActionType,
              ...(formik.values.repeatSchedule
                ? { repeatSchedule: formik.values.repeatSchedule }
                : {}),
              ...(formik.values.dueDate
                ? { dueDate: dayjs(formik.values.dueDate).format('YYYY-MM-DD') }
                : {}),
              dueDateTime: dayjs(time).format('HH:mm:ss'),
              storeIds: storeId ? [storeId] : selectedStores.map(s => s),
              createOnePerStore: true,
            }
            allPromises.push(
              createAction({
                variables: {
                  input: createActionInput,
                },
              }),
            )
          })
        await Promise.all(allPromises)

        setStatus({ success: true })
        onSuccess?.()
        formik.resetForm()
        setSelectedStores([])
        onClose()
      } catch (error: any) {
        const message = error.message || t('common.somethingWentWrong')
        setStatus({ success: false, message })
      }
      resetAll()
      refresh()
    },
  })

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getDialogTitle = () => {
    switch (activeStep) {
      case 0:
        return t('createRestockAction.title_1')
      case 1:
        return t(
          'createRestockAction.selectActionTypeStepTitle',
          'Select Restock Type',
        )
      case 2:
        return t('createRestockAction.title_2')
      case 3:
        return t('createRestockAction.title_3')
      case 4:
        return t('createRestockAction.reviewStepTitle', 'Review Details')
    }
  }

  const getDialogDescription = () => {
    switch (activeStep) {
      case 0:
        return t('createRestockAction.description')
      case 1:
        return t(
          'createRestockAction.selectActionTypeStepDescription',
          'Choose the type of restock action you want to create.',
        )
      case 4:
        return t(
          'createRestockAction.reviewStepDescription',
          'Please review the restock details before saving.',
        )
      default:
        return undefined
    }
  }

  const StepZero = ({
    formik,
  }: {
    formik: TypedFormikReturn<CreateFormValues, DefaultStatus>
  }) => {
    return (
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="center"
        alignItems="stretch"
      >
        <ActionTypeCard
          type={ActionType.PRODUCT_CAPTURE}
          title={getActionTypeLabel(ActionType.PRODUCT_CAPTURE)}
          description={t(
            'createRestockAction.actionType.planogramComplianceDesc',
            'Requires the user to do a before & after photo of the cooler to measure compliance',
          )}
          formik={formik}
          onNextStep={handleGoForwardStep}
        />
        <ActionTypeCard
          type={ActionType.SIMPLE}
          title={getActionTypeLabel(ActionType.SIMPLE)}
          description={t(
            'createRestockAction.actionType.noPhotoDesc',
            'No Photo Required',
          )}
          formik={formik}
          onNextStep={handleGoForwardStep}
        />
      </Stack>
    )
  }

  const StepOne = ({
    formik,
  }: {
    formik: TypedFormikReturn<CreateFormValues, DefaultStatus>
  }) => {
    const beverageCategoryId = categories.find(c =>
      c.label.toLowerCase().includes('bev'),
    )?.value
    const alcoholCategoryId = categories.find(c =>
      c.label.toLowerCase().includes('alcohol'),
    )?.value
    return (
      <Stack direction={'row'} spacing={2} justifyContent="space-between">
        <Card variant="outlined">
          <CardActionArea
            onClick={() => {
              formik.setValues({
                ...formik.values,
                title: TolgeeHelpers.sanitizeTranslationForUrls(
                  t('createRestockAction.restockType_1'),
                ),
                ...(beverageCategoryId
                  ? { masterCategoryId: beverageCategoryId }
                  : {}),
              })
              handleGoForwardStep()
            }}
          >
            <CardContent>
              <Stack alignItems={'center'}>
                <Typography variant="h6">
                  {t('createRestockAction.restockType_1')}
                </Typography>
                <img src={cold_vault_1} style={{ width: 150 }} />
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card variant="outlined">
          <CardActionArea
            onClick={() => {
              formik.setValues({
                ...formik.values,
                title: TolgeeHelpers.sanitizeTranslationForUrls(
                  t('createRestockAction.restockType_2'),
                ),
                ...(alcoholCategoryId
                  ? { masterCategoryId: alcoholCategoryId }
                  : {}),
              })
              handleGoForwardStep()
            }}
          >
            <CardContent>
              <Stack alignItems={'center'}>
                <Typography variant="h6">
                  {t('createRestockAction.restockType_2')}
                </Typography>
                <img src={cold_vault_2} style={{ width: 150 }} />
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      </Stack>
    )
  }

  const StepTwo = () => {
    const [searchTerm, setSearchTerm] = React.useState('')
    const [maxStores, setMaxStores] = React.useState(0)
    const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

    const {
      stores,
      state: { TenantStoresLazyQuery },
    } = useGetTenantStores({
      onlyMyStores: true,
      isActive: true,
      searchTerm: debouncedSearchTerm,
    })
    useEffect(() => {
      if (stores.length > maxStores) {
        setMaxStores(stores.length)
      }
    }, [stores.length, maxStores])

    return (
      <>
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
          <Typography variant="body2">
            {`${selectedStores.length} of ${maxStores} ${t('common.selected')}`}
          </Typography>
          <Button
            type="button"
            onClick={() => {
              setSelectedStores(stores.map(s => s.id))
            }}
          >
            {t('common.selectAll')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              setSelectedStores([])
            }}
          >
            {t('common.selectNone')}
          </Button>
        </Stack>
        <TextField
          id="restockStoresSearch"
          type="text"
          name="restockStoresSearch"
          label={t('stores.searchStore')}
          value={searchTerm}
          fullWidth
          placeholder={t('stores.searchStoreHelperText')}
          onChange={e => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm('')} size="small">
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ minHeight: 400 }}>
          {TenantStoresLazyQuery.lazyQuery.meta.loading ? (
            <Stack>
              <Skeleton height={80} />
              <Skeleton height={80} />
              <Skeleton height={80} />
            </Stack>
          ) : (
            <>
              {stores.map(store => {
                return (
                  <Stack
                    key={store.id}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      storeSelected(store.id)
                    }}
                  >
                    <Checkbox checked={selectedStores.includes(store.id)} />
                    {store.location && (
                      <StoreHeader
                        name={store.location.name}
                        city={store.location.city}
                        state={store.location.state}
                        address1={store.location.address1}
                        postalCode={store.location.postalCode}
                      />
                    )}
                  </Stack>
                )
              })}
            </>
          )}
        </Box>
      </>
    )
  }

  const [dueTimes, setDueTimes] = React.useState<string[]>([''])
  const setTimeAtIndex = (index: number, time: string) => {
    setDueTimes(prev => {
      const newTimes = [...prev]
      newTimes[index] = time
      return newTimes
    })
  }

  const StepFour = ({
    formik,
    dueTimes,
    selectedStores,
  }: {
    formik: TypedFormikReturn<CreateFormValues, DefaultStatus>
    dueTimes: string[]
    selectedStores: string[]
  }) => {
    return (
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography>
            <strong>Action Type:</strong>{' '}
            {getActionTypeLabel(formik.values.actionType as ActionType)}
          </Typography>
          <Typography>
            <strong>Category:</strong> {formik.values.title}
          </Typography>
          <Typography>
            <strong>Stores Selected:</strong> {selectedStores.length}
          </Typography>
          <Typography>
            <strong>Start Date:</strong>{' '}
            {formik.values.dueDate
              ? dayjs(formik.values.dueDate).format('MMM D, YYYY')
              : 'N/A'}
          </Typography>
          <Typography>
            <strong>Repeat:</strong> {formik.values.repeatSchedule || 'None'}
          </Typography>
          <Typography>
            <strong>Due Times:</strong>{' '}
            {dueTimes
              .filter(t => t !== '')
              .map(time => dayjs(time).format('h:mm A'))
              .join(', ')}
          </Typography>
        </Stack>
      </Stack>
    )
  }

  const getDialogBody = (
    formik: TypedFormikReturn<CreateFormValues, DefaultStatus>,
  ) => {
    switch (activeStep) {
      case 0:
        return <StepOne formik={formik} />
      case 1:
        return <StepZero formik={formik} />
      case 2:
        return <StepTwo />
      case 3:
        return (
          <StepThree
            formik={formik}
            setDueTimes={setDueTimes}
            dueTimes={dueTimes}
            setTimeAtIndex={setTimeAtIndex}
          />
        )
      case 4:
        return (
          <StepFour
            formik={formik}
            dueTimes={dueTimes}
            selectedStores={selectedStores}
          />
        )
      default:
        return <></>
    }
  }

  const handleGoBackStep = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleGoForwardStep = () => {
    setCompleted(prev => ({ ...prev, [activeStep]: true }))
    setActiveStep(prev => prev + 1)
  }

  const activeStepDone = () => {
    switch (activeStep) {
      case 0:
        return !!formik.values.title
      case 1:
        return !!formik.values.actionType
      case 2:
        return selectedStores.length !== 0
      case 3:
        return dueTimes.filter(t => t !== '').length > 0
      default:
        return true
    }
  }

  const progressHeader = () => {
    if (activeStep === 4) return null
    return (
      <>
        {activeStep > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {formik.values.title && (
              <Chip
                label={`Category: ${formik.values.title}`}
                color="primary"
                variant="outlined"
              />
            )}
            {activeStep > 1 && !!formik.values.actionType && (
              <Chip
                label={`Type: ${getActionTypeLabel(formik.values.actionType as ActionType)}`}
                color="primary"
                variant="outlined"
              />
            )}
            {activeStep > 2 && selectedStores.length > 0 && (
              <Chip
                label={`Stores: ${selectedStores.length}`}
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </>
    )
  }

  const onLastStep = activeStep === steps.length - 1
  const resetAll = () => {
    formik.setValues({ ...initialValues })
    setSelectedStores([])
    setCompleted({})
    setActiveStep(0)
    setDueTimes([''])
  }
  return (
    <Dialog open={open} onClose={onClose} fullWidth fullScreen={isMobile}>
      <form noValidate onSubmit={formik.handleSubmit}>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {getDialogDescription()}
          </Typography>
          <Stack spacing={4} mt={2}>
            {formik.status?.message && (
              <Alert severity={formik.status.success ? 'success' : 'warning'}>
                {formik.status.message}
              </Alert>
            )}
            {progressHeader()}
            {/* <Box sx={{ height: 2, width: '100%' }} /> */}
            {getDialogBody(formik)}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', width: '100%' }}>
          <Box sx={{ width: '100%', overflowX: 'auto', pb: 2 }}>
            <Stepper
              nonLinear
              activeStep={activeStep}
              alternativeLabel={isMobile}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <StepButton color="inherit">{label}</StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Stack
            direction={'row'}
            spacing={2}
            width="100%"
            justifyContent="space-between"
            mt={4}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                onClose()
                resetAll()
              }}
              disabled={formik.isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Stack direction={'row'} spacing={2}>
              {activeStep > 0 && (
                <Button type="button" onClick={handleGoBackStep}>
                  {t('common.back')}
                </Button>
              )}
              <Button
                type="button"
                variant="contained"
                loading={formik.isSubmitting}
                loadingPosition="end"
                disabled={!activeStepDone()}
                onClick={() => {
                  onLastStep ? formik.handleSubmit() : handleGoForwardStep()
                }}
              >
                {onLastStep
                  ? t('common.saveChanges')
                  : activeStep === steps.length - 2
                    ? t('common.review', 'Review')
                    : t('common.next')}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  )
}
